import React, { useState, useEffect } from 'react';
import { Check, X, Star } from 'lucide-react';
import { Review } from '../../types';
import { useLocalStorage } from '../../hooks/useLocalStorage';

export default function AdminReviews() {
  const [pendingReviews, setPendingReviews] = useLocalStorage<Review[]>('pendingReviews', []);
  const [taxiStands, setTaxiStands] = useLocalStorage('taxiStands', []);

  const handleApprove = (review: Review) => {
    // Update taxi stand reviews
    const updatedTaxiStands = taxiStands.map(stand => {
      if (stand.id === review.taxiStandId) {
        // Update rating
        const approvedReviews = [...stand.reviews, { ...review, status: 'approved' }];
        const totalRating = approvedReviews.reduce((sum, r) => sum + r.rating, 0);
        const newRating = totalRating / approvedReviews.length;

        return {
          ...stand,
          reviews: approvedReviews,
          rating: Math.round(newRating * 10) / 10,
          totalReviews: approvedReviews.length
        };
      }
      return stand;
    });

    setTaxiStands(updatedTaxiStands);
    
    // Remove from pending reviews
    setPendingReviews(pendingReviews.filter(r => r.id !== review.id));
  };

  const handleReject = (review: Review) => {
    // Add to taxi stand's rejected reviews
    const updatedTaxiStands = taxiStands.map(stand => {
      if (stand.id === review.taxiStandId) {
        return {
          ...stand,
          reviews: [...stand.reviews, { ...review, status: 'rejected' }]
        };
      }
      return stand;
    });

    setTaxiStands(updatedTaxiStands);
    
    // Remove from pending reviews
    setPendingReviews(pendingReviews.filter(r => r.id !== review.id));
  };

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-white">Yorumlar</h1>
        <div className="bg-yellow-400/10 text-yellow-400 px-3 py-1 rounded-full text-sm">
          {pendingReviews.length} Bekleyen Yorum
        </div>
      </div>

      <div className="bg-gray-800 rounded-lg overflow-hidden">
        <div className="p-6 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-white">Onay Bekleyen Yorumlar</h2>
        </div>

        <div className="divide-y divide-gray-700">
          {pendingReviews.map(review => {
            const stand = taxiStands.find(s => s.id === review.taxiStandId);
            
            return (
              <div key={review.id} className="p-6 hover:bg-gray-750 transition-colors">
                <div className="flex justify-between items-start gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-white font-medium">{stand?.name}</h3>
                      <div className="flex items-center gap-1 bg-yellow-400/10 text-yellow-400 px-2 py-0.5 rounded-full text-xs">
                        <Star className="w-3 h-3 fill-current" />
                        <span>{review.rating}</span>
                      </div>
                    </div>
                    
                    <p className="text-sm text-gray-400 mb-2">{review.comment}</p>
                    
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>{review.userName}</span>
                      <span>{new Date(review.date).toLocaleDateString('tr-TR')}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleApprove(review)}
                      className="p-2 bg-green-500/10 hover:bg-green-500/20 text-green-500 rounded-lg transition-colors"
                    >
                      <Check className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleReject(review)}
                      className="p-2 bg-red-500/10 hover:bg-red-500/20 text-red-500 rounded-lg transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          {pendingReviews.length === 0 && (
            <div className="p-6 text-center text-gray-400">
              Onay bekleyen yorum bulunmuyor.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}